import React, { useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useLocation,
} from 'react-router-dom';
import HomePage from './components/HomePage';
import ProductList from './components/ProductList';
import ProductsPage from './components/ProductsPage';
import Cart from './components/Cart';
import SuccessPage from './components/SuccessPage';
import ContactPage from './components/ContactPage';
import RefundPolicyPage from './components/RefundPolicyPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import ShippingPolicyPage from './components/ShippingPolicyPage';
import ProductDetailPage from './components/ProductDetailPage';
import Footer from './components/Footer';
import { Product, CartItem } from './types';
import styles from './App.module.css';

// Component to conditionally apply layout classes
const MainContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isProductDetail = location.pathname.startsWith('/product/');

  return (
    <main
      className={`${styles.flexGrow} ${
        isProductDetail ? styles.productDetailLayout : ''
      }`}
    >
      {children}
    </main>
  );
};

// Wrapper component that uses the router context
const AppContent: React.FC = () => {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    const savedItems = window.localStorage.getItem('cartItems');
    return savedItems ? JSON.parse(savedItems) : [];
  });

  // Update localStorage whenever cart items change
  React.useEffect(() => {
    window.localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  const handleAddToCart = (product: Product, size: string) => {
    setCartItems(items => {
      // Create a unique key for cart items based on product ID and size
      const sizeKey = size || 'no-size';
      const existingItem = items.find(
        item => item.id === product.id && (item.size || 'no-size') === sizeKey
      );

      if (existingItem) {
        return items.map(item =>
          item.id === product.id && (item.size || 'no-size') === sizeKey
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }

      // Add new item with or without size
      return [...items, { ...product, quantity: 1, size: size || undefined }];
    });
  };

  const handleUpdateQuantity = (productId: string, quantity: number) => {
    setCartItems(items =>
      quantity > 0
        ? items.map(item =>
            item.id === productId ? { ...item, quantity } : item
          )
        : items.filter(item => item.id !== productId)
    );
  };

  const handleRemoveItem = (productId: string) => {
    setCartItems(items => items.filter(item => item.id !== productId));
  };

  const handleCheckout = async () => {
    // Implement checkout logic here
    console.log('Checking out...', cartItems);
  };

  const refreshCart = () => {
    // Force a refresh of cart state from localStorage
    const savedItems = window.localStorage.getItem('cartItems');
    setCartItems(savedItems ? JSON.parse(savedItems) : []);
  };

  return (
    <div className={styles.app}>
      <header className={styles.header}>
        <nav className={styles.headerNav}>
          <div className={styles.headerNavContainer}>
            <Link to="/" className={`${styles.headerLink}`}>
              Home
            </Link>
            <Link to="/products" className={`${styles.headerLink}`}>
              Products
            </Link>
            <Link to="/cart" className={`${styles.headerLink}`}>
              Cart ({cartItems.reduce((sum, item) => sum + item.quantity, 0)})
            </Link>
            <Link to="/contact" className={`${styles.headerLink}`}>
              Contact
            </Link>
          </div>
        </nav>
      </header>
      <MainContent>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route
            path="/cart"
            element={
              <Cart
                items={cartItems}
                onUpdateQuantity={handleUpdateQuantity}
                onRemoveItem={handleRemoveItem}
                onCheckout={handleCheckout}
              />
            }
          />
          <Route
            path="/success"
            element={<SuccessPage onCartRefresh={refreshCart} />}
          />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
          <Route
            path="/product/:id"
            element={<ProductDetailPage onAddToCart={handleAddToCart} />}
          />
        </Routes>
      </MainContent>
      <Footer />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;
